if (!customElements.get('quick-add-modal')) {
  customElements.define('quick-add-modal', class QuickAddModal extends ModalDialog {
    constructor() {
      super();
      this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
    }

    hide(preventFocus = false) {
      const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (cartNotification) cartNotification.setActiveElement(this.openedBy);
      this.modalContent.innerHTML = '';

      if (preventFocus) this.openedBy = null;
      super.hide();
    }

    show(opener) {
      opener.setAttribute('aria-disabled', true);
      opener.classList.add('loading');
      opener.querySelector('.loading-overlay__spinner').classList.remove('hidden');

      fetch(opener.getAttribute('data-product-url'))
        .then((response) => response.text())
        .then((responseText) => {
          const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
          this.productElement = responseHTML.querySelector('section[id^="MainProduct-"]');
          this.preventDuplicatedIDs();
          this.removeDOMElements();
          this.setInnerHTML(this.modalContent, this.productElement.innerHTML);

          if (window.Shopify && Shopify.PaymentButton) {
            Shopify.PaymentButton.init();
          }

          if (window.ProductModel) window.ProductModel.loadShopifyXR();

          this.removeGalleryListSemantic();
          this.updateImageSizes();
          this.preventVariantURLSwitching();
          super.show(opener);
        })
        .finally(() => {
          opener.removeAttribute('aria-disabled');
          opener.classList.remove('loading');
          opener.querySelector('.loading-overlay__spinner').classList.add('hidden');
        });
    }

    setInnerHTML(element, html) {
      element.innerHTML = html;

      // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
      element.querySelectorAll('script').forEach(oldScriptTag => {
        const newScriptTag = document.createElement('script');
        Array.from(oldScriptTag.attributes).forEach(attribute => {
          newScriptTag.setAttribute(attribute.name, attribute.value)
        });
        newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
        oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
      });
    }

    preventVariantURLSwitching() {
      this.modalContent.querySelector('variant-radios,variant-selects').setAttribute('data-update-url', 'false');
    }

    removeDOMElements() {
      const pickupAvailability = this.productElement.querySelector('pickup-availability');
      if (pickupAvailability) pickupAvailability.remove();

      const productModal = this.productElement.querySelector('product-modal');
      if (productModal) productModal.remove();
    }

    preventDuplicatedIDs() {
      const sectionId = this.productElement.dataset.section;
      this.productElement.innerHTML = this.productElement.innerHTML.replaceAll(sectionId, `quickadd-${ sectionId }`);
      this.productElement.querySelectorAll('variant-selects, variant-radios').forEach((variantSelect) => {
        variantSelect.dataset.originalSection = sectionId;
      });
    }

    removeGalleryListSemantic() {
      const galleryList = this.modalContent.querySelector('[id^="Slider-Gallery"]');
      if (!galleryList) return;

      galleryList.setAttribute('role', 'presentation');
      galleryList.querySelectorAll('[id^="Slide-"]').forEach(li => li.setAttribute('role', 'presentation'));
    }

    updateImageSizes() {
      const product = this.modalContent.querySelector('.product');
      const desktopColumns = product.classList.contains('product--columns');
      if (!desktopColumns) return;

      const mediaImages = product.querySelectorAll('.product__media img');
      if (!mediaImages.length) return;

      let mediaImageSizes = '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

      if (product.classList.contains('product--medium')) {
        mediaImageSizes = mediaImageSizes.replace('715px', '605px');
      } else if (product.classList.contains('product--small')) {
        mediaImageSizes = mediaImageSizes.replace('715px', '495px');
      }

      mediaImages.forEach(img => img.setAttribute('sizes', mediaImageSizes));
    }
  });
}

if (!customElements.get('inline-quick-add')) {
  customElements.define('inline-quick-add', class InlineQuickAdd extends HTMLElement {
    constructor() {
      super();
      this.sizeButtons = this.querySelectorAll('.inline-quick-add__size-btn');
      this.action = this.querySelector('.inline-quick-add__action');
      this.actionLabel = this.querySelector('.inline-quick-add__action-label');
      this.cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      this.selectedVariantId = null;

      // First tap: pick a size (highlights it, flips the label to "Add to Cart").
      this.sizeButtons.forEach(btn =>
        btn.addEventListener('click', this.selectSize.bind(this))
      );

      // Second tap: confirm — adds to cart, or opens the notify-me modal if the size is sold out.
      if (this.action) {
        this.action.addEventListener('click', this.handleAction.bind(this));
      }
    }

    selectSize(event) {
      event.preventDefault();
      event.stopPropagation();
      const btn = event.currentTarget;

      // Tapping the already-selected size deselects it (back to "Select Size").
      if (btn.classList.contains('is-selected')) {
        this.clearSelection();
        return;
      }

      this.sizeButtons.forEach((b) => {
        b.classList.remove('is-selected');
        b.setAttribute('aria-pressed', 'false');
      });

      // Every size is selectable now — sold-out ones just switch the action to "Notify Me".
      btn.classList.add('is-selected');
      btn.setAttribute('aria-pressed', 'true');
      this.selectedVariantId = btn.dataset.variantId;
      this.selectedIsSoldOut = btn.dataset.soldout === 'true';
      this.selectedSize = btn.dataset.size || (btn.textContent || '').trim();
      this.setActionState('ready');
    }

    clearSelection() {
      this.sizeButtons.forEach((b) => {
        b.classList.remove('is-selected');
        b.setAttribute('aria-pressed', 'false');
      });
      this.selectedVariantId = null;
      this.selectedIsSoldOut = false;
      this.setActionState('empty');
    }

    setActionState(state) {
      if (!this.action) return;
      this.action.dataset.state = state;
      if (state === 'ready') {
        this.action.setAttribute('aria-disabled', 'false');
        if (this.selectedIsSoldOut) {
          this.action.classList.add('is-notify');
          this.actionLabel.textContent = 'Notify Me';
        } else {
          this.action.classList.remove('is-notify');
          this.actionLabel.textContent = 'Add to Cart';
        }
      } else if (state === 'empty') {
        this.action.setAttribute('aria-disabled', 'true');
        this.action.classList.remove('is-notify');
        this.actionLabel.textContent = 'Select Size';
      }
    }

    // Second-tap handler: in-stock size adds to cart; sold-out size opens the notify-me modal.
    handleAction(event) {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      if (!this.selectedVariantId) return; // dead click until a size is chosen
      if (this.selectedIsSoldOut) {
        if (typeof window.openGratefulBackInStock === 'function') {
          window.openGratefulBackInStock({
            variantId: this.selectedVariantId,
            size: this.selectedSize || '',
            productTitle: this.getAttribute('data-product-title') || ''
          });
        }
        return;
      }
      this.addToCart();
    }

    async addToCart(event) {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }

      // Dead click: do nothing (and don't fall through to any card link) until a size is chosen.
      const variantId = this.selectedVariantId;
      if (!variantId || this.isAdding) return;

      this.isAdding = true;
      const btn = this.action;
      const originalLabel = 'Add to Cart';

      btn.classList.add('is-loading');
      this.actionLabel.textContent = 'Adding…';

      try {
        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', 1);

        if (this.cart && typeof this.cart.getSectionsToRender === 'function') {
          const sections = this.cart.getSectionsToRender().map(s => s.id);
          formData.append('sections', sections.join(','));
          formData.append('sections_url', window.location.pathname);
        }

        const response = await fetch(`${window.routes.cart_add_url}`, {
          method: 'POST',
          body: formData,
          headers: {
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          }
        });

        const data = await response.json();

        if (!response.ok || data.status) {
          throw new Error(data.description || data.message || `Cart error (${response.status})`);
        }

        btn.classList.remove('is-loading');
        btn.classList.add('is-added');
        this.actionLabel.textContent = '✓ Added';

        // Fire analytics event so tracking pixels (Meta, Google, TikTok, Klaviyo, Triple Whale, Attentive, etc.) pick up the ATC
        try {
          if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
            const currency = (window.Shopify.currency && window.Shopify.currency.active) || 'USD';
            const items = data.items || [data];
            items.forEach(item => {
              window.Shopify.analytics.publish('product_added_to_cart', {
                cartLine: {
                  merchandise: {
                    id: 'gid://shopify/ProductVariant/' + (item.variant_id || item.id),
                    price: { amount: (item.price || 0) / 100, currencyCode: currency },
                    product: {
                      id: 'gid://shopify/Product/' + item.product_id,
                      title: item.product_title || item.title || '',
                      untranslatedTitle: item.product_title || item.title || '',
                      vendor: item.vendor || '',
                      type: item.product_type || ''
                    },
                    sku: item.sku || '',
                    title: item.variant_title || '',
                    untranslatedTitle: item.variant_title || ''
                  },
                  quantity: item.quantity || 1,
                  cost: {
                    totalAmount: {
                      amount: ((item.final_line_price || item.line_price || item.price) || 0) / 100,
                      currencyCode: currency
                    }
                  }
                }
              });
            });
          }
          document.dispatchEvent(new CustomEvent('cart:item-added', { detail: data }));
        } catch (analyticsErr) {
          console.warn('Analytics publish failed (non-fatal):', analyticsErr);
        }

        if (this.cart && typeof this.cart.renderContents === 'function') {
          try {
            // Dawn's renderContents misses the is-empty class on the outer
            // <cart-drawer> and <cart-drawer-items>. On first add from an
            // empty cart, items don't show until manually cleared.
            this.cart.classList.remove('is-empty');
            const cartDrawerItems = this.cart.querySelector('cart-drawer-items');
            if (cartDrawerItems) cartDrawerItems.classList.remove('is-empty');

            this.cart.renderContents(data);
          } catch (renderErr) {
            console.warn('Cart render failed, will reload:', renderErr);
            window.location.reload();
            return;
          }
        }

        // Reset the card back to its resting state so it's ready for the next add.
        setTimeout(() => {
          btn.classList.remove('is-added');
          this.isAdding = false;
          this.clearSelection();
        }, 1500);

      } catch (err) {
        console.error('Add to cart failed:', err);
        btn.classList.remove('is-loading');
        this.isAdding = false;
        // Keep the size selected so the shopper can simply tap again to retry.
        this.actionLabel.textContent = originalLabel;
        alert('Add to cart failed: ' + err.message);
      }
    }
  });
}

/* Pre-release ("soon" tag) product cards: a single product-level
   "Notify me when it drops" button that opens the waitlist modal. */
if (!customElements.get('coming-soon-notify')) {
  customElements.define('coming-soon-notify', class ComingSoonNotify extends HTMLElement {
    constructor() {
      super();
      var self = this;
      var btn = this.querySelector('.coming-soon-notify__btn');
      if (btn) {
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          if (typeof window.openGratefulWaitlist === 'function') {
            window.openGratefulWaitlist({
              productTitle: self.getAttribute('data-product-title') || '',
              productHandle: self.getAttribute('data-product-handle') || '',
              productId: self.getAttribute('data-product-id') || ''
            });
          }
        });
      }
    }
  });
}

/* ===========================================================
   BACK IN STOCK (collection cards) — andrew
   A sold-out size opens this modal; submitting subscribes the
   shopper to Klaviyo back-in-stock for that exact Shopify variant
   (email always; SMS optional, with consent).
   No app/backend needed — uses Klaviyo's client-side endpoints.
   =========================================================== */
(function () {
  /* ---------- FILL THESE IN (both are safe to expose publicly) ---------- */
  var KLAVIYO_PUBLIC_KEY = 'LVVTL5';   // Klaviyo Site ID (6-char Public API Key)
  var KLAVIYO_SMS_LIST_ID = 'R4irNs';  // Klaviyo SMS list — records text consent (back in stock)
  var KLAVIYO_WAITLIST_LIST_ID = 'XkziPp'; // Klaviyo "Coming Soon Waitlist" list (pre-release signups)
  /* --------------------------------------------------------------------- */
  var KLAVIYO_REVISION = '2024-06-15';

  var modal = null;
  var els = {};
  var current = {};

  function buildModal() {
    var overlay = document.createElement('div');
    overlay.className = 'klaviyo-bis-overlay';
    overlay.innerHTML = [
      '<div class="klaviyo-bis" role="dialog" aria-modal="true" aria-label="Back in stock notification">',
      '  <button type="button" class="klaviyo-bis__close" aria-label="Close">&times;</button>',
      '  <p class="klaviyo-bis__title">Notify me when it’s back</p>',
      '  <p class="klaviyo-bis__sub"></p>',
      '  <input type="email" class="klaviyo-bis__field klaviyo-bis__email" placeholder="Email address" autocomplete="email" inputmode="email">',
      '  <label class="klaviyo-bis__check"><input type="checkbox" class="klaviyo-bis__smsopt"><span>Text me too (optional)</span></label>',
      '  <div class="klaviyo-bis__phonewrap"><input type="tel" class="klaviyo-bis__field klaviyo-bis__phone" placeholder="Mobile number" autocomplete="tel" inputmode="tel"></div>',
      '  <p class="klaviyo-bis__fine">By entering your number and checking the box, you agree to receive recurring automated marketing texts from Grateful at the number provided. Consent is not a condition of purchase. Msg &amp; data rates may apply. Reply STOP to cancel.</p>',
      '  <button type="button" class="klaviyo-bis__submit">Notify Me</button>',
      '  <p class="klaviyo-bis__msg" hidden></p>',
      '</div>'
    ].join('');
    document.body.appendChild(overlay);

    els.overlay = overlay;
    els.title = overlay.querySelector('.klaviyo-bis__title');
    els.sub = overlay.querySelector('.klaviyo-bis__sub');
    els.email = overlay.querySelector('.klaviyo-bis__email');
    els.smsopt = overlay.querySelector('.klaviyo-bis__smsopt');
    els.phonewrap = overlay.querySelector('.klaviyo-bis__phonewrap');
    els.phone = overlay.querySelector('.klaviyo-bis__phone');
    els.submit = overlay.querySelector('.klaviyo-bis__submit');
    els.msg = overlay.querySelector('.klaviyo-bis__msg');

    overlay.querySelector('.klaviyo-bis__close').addEventListener('click', close);
    overlay.addEventListener('click', function (e) { if (e.target === overlay) close(); });
    els.smsopt.addEventListener('change', function () {
      els.phonewrap.classList.toggle('show', els.smsopt.checked);
    });
    els.submit.addEventListener('click', submit);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && modal && modal.classList.contains('open')) close();
    });

    modal = overlay;
  }

  function openModal(opts, mode) {
    if (!modal) buildModal();
    current = opts || {};
    current.mode = mode || 'bis';

    var isWaitlist = current.mode === 'waitlist';
    // waitlist mode hides the SMS controls (SMS for soon drops is handled via Attentive separately)
    els.overlay.classList.toggle('waitlist', isWaitlist);
    els.title.textContent = isWaitlist ? 'Notify me when it drops' : 'Notify me when it’s back';

    var bits = [];
    if (current.productTitle) bits.push(current.productTitle);
    if (current.size) bits.push('Size ' + current.size);
    els.sub.textContent = bits.join('  ·  ');

    els.email.value = '';
    els.phone.value = '';
    els.smsopt.checked = false;
    els.phonewrap.classList.remove('show');
    setMsg('', false);
    els.submit.disabled = false;
    els.submit.textContent = 'Notify Me';
    modal.classList.add('open');
    setTimeout(function () { els.email.focus(); }, 50);
  }

  function close() { if (modal) modal.classList.remove('open'); }

  function setMsg(text, isError) {
    els.msg.textContent = text || '';
    els.msg.hidden = !text;
    els.msg.classList.toggle('error', !!isError);
  }

  function normPhone(p) {
    p = (p || '').trim();
    if (!p) return '';
    var digits = p.replace(/[^\d+]/g, '');
    if (digits.charAt(0) === '+') return digits;
    if (digits.length === 10) return '+1' + digits;
    if (digits.length === 11 && digits.charAt(0) === '1') return '+' + digits;
    return digits ? ('+' + digits) : '';
  }

  function submit() {
    if (current.mode === 'waitlist') { return submitWaitlist(); }
    return submitBis();
  }

  // ---- Coming-soon waitlist: record a per-product event + subscribe email to the waitlist list ----
  async function submitWaitlist() {
    var email = (els.email.value || '').trim();
    if (!email || email.indexOf('@') === -1) { setMsg('Please enter a valid email.', true); return; }
    if (KLAVIYO_PUBLIC_KEY.indexOf('REPLACE') === 0) { setMsg('Setup needed: add your Klaviyo Public Key in quick-add.js.', true); return; }

    els.submit.disabled = true;
    els.submit.textContent = 'Submitting…';

    try {
      // 1) "Waitlist Signup" event, tagged with the product — this is the metric you read to decide go/no-go.
      var eventPayload = {
        data: {
          type: 'event',
          attributes: {
            properties: {
              Product: current.productTitle || '',
              Handle: current.productHandle || '',
              ProductID: current.productId || ''
            },
            metric: { data: { type: 'metric', attributes: { name: 'Waitlist Signup' } } },
            profile: { data: { type: 'profile', attributes: { email: email } } }
          }
        }
      };
      var evRes = await fetch('https://a.klaviyo.com/client/events/?company_id=' + KLAVIYO_PUBLIC_KEY, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: KLAVIYO_REVISION },
        body: JSON.stringify(eventPayload)
      });
      if (evRes.status !== 202 && !evRes.ok) { throw new Error('Signup failed (' + evRes.status + ')'); }

      // 2) Subscribe the email to the waitlist list so you can campaign them at launch.
      if (KLAVIYO_WAITLIST_LIST_ID.indexOf('REPLACE') !== 0) {
        var subPayload = {
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'Coming Soon Waitlist',
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email: email
                  }
                }
              }
            },
            relationships: { list: { data: { type: 'list', id: KLAVIYO_WAITLIST_LIST_ID } } }
          }
        };
        try {
          var subRes = await fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + KLAVIYO_PUBLIC_KEY, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', revision: KLAVIYO_REVISION },
            body: JSON.stringify(subPayload)
          });
          if (subRes.status !== 202 && !subRes.ok) {
            console.warn('Waitlist list subscribe failed (event still recorded):', subRes.status);
          }
        } catch (subErr) {
          console.error('[Grateful waitlist] list subscribe network error:', subErr);
        }
      }

      setMsg('You’re on the list — we’ll let you know when it drops.', false);
      els.submit.textContent = 'Done ✓';
      setTimeout(close, 1800);
    } catch (err) {
      console.error('Waitlist signup failed:', err);
      setMsg('Something went wrong. Please try again.', true);
      els.submit.disabled = false;
      els.submit.textContent = 'Notify Me';
    }
  }

  async function submitBis() {
    var email = (els.email.value || '').trim();
    var wantsSms = els.smsopt.checked;
    var phone = normPhone(els.phone.value);

    if (!email || email.indexOf('@') === -1) { setMsg('Please enter a valid email.', true); return; }
    if (wantsSms && !phone) { setMsg('Add a mobile number or uncheck "Text me too".', true); return; }
    if (KLAVIYO_PUBLIC_KEY.indexOf('REPLACE') === 0) { setMsg('Setup needed: add your Klaviyo Public Key in quick-add.js.', true); return; }

    els.submit.disabled = true;
    els.submit.textContent = 'Submitting…';

    try {
      var channels = ['EMAIL'];
      var profileAttrs = { email: email };
      if (wantsSms && phone) { channels.push('SMS'); profileAttrs.phone_number = phone; }

      var bisPayload = {
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            profile: { data: { type: 'profile', attributes: profileAttrs } },
            channels: channels
          },
          relationships: {
            variant: { data: { type: 'catalog-variant', id: '$shopify:::$default:::' + current.variantId } }
          }
        }
      };

      var res = await fetch('https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=' + KLAVIYO_PUBLIC_KEY, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: KLAVIYO_REVISION },
        body: JSON.stringify(bisPayload)
      });
      if (res.status !== 202 && !res.ok) { throw new Error('Subscription failed (' + res.status + ')'); }

      // SMS requires explicit consent recorded against a list, so they can legally be texted.
      if (wantsSms && phone && KLAVIYO_SMS_LIST_ID.indexOf('REPLACE') !== 0) {
        var consentPayload = {
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'Back in Stock (collection)',
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    phone_number: phone
                  }
                }
              }
            },
            relationships: { list: { data: { type: 'list', id: KLAVIYO_SMS_LIST_ID } } }
          }
        };
        try {
          await fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + KLAVIYO_PUBLIC_KEY, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', revision: KLAVIYO_REVISION },
            body: JSON.stringify(consentPayload)
          });
        } catch (smsErr) {
          console.warn('SMS consent call failed (email BIS still recorded):', smsErr);
        }
      }

      setMsg('You’re on the list — we’ll let you know the moment it’s back.', false);
      els.submit.textContent = 'Done ✓';
      setTimeout(close, 1800);
    } catch (err) {
      console.error('Back in stock failed:', err);
      setMsg('Something went wrong. Please try again.', true);
      els.submit.disabled = false;
      els.submit.textContent = 'Notify Me';
    }
  }

  window.openGratefulBackInStock = function (opts) { openModal(opts, 'bis'); };
  window.openGratefulWaitlist = function (opts) { openModal(opts, 'waitlist'); };
})();